import { employeeWorkTime, monthlyWorkTimeHours, workTimeHoursLabel } from "./workTimeMeasure";
import { calculateShiftHours, calculateOvertimeForShift } from "./timeReportHours";

export const defaultNotificationSettings = {
  enabled: false,
  employment: false,
  overtime: false,
  overtimeLimit: false,
  dailyRest: false,
  dailyRestHours: "",
  overtimeStartHours: "",
  overtimeLimitHours: "",
};
export const hoursLabel = (hours) => hours.toLocaleString("sv-SE", { maximumFractionDigits: 1 });
const identifiers = (person) => [person?.id, person?.employeeId, person?.uid, person?.authUid, person?.userId, person?.email]
  .filter(Boolean).map((value) => String(value).trim().toLowerCase());

export function notificationSettingsError(settings) {
  if (!settings.enabled) return "";
  if (settings.overtime && (!Number.isFinite(Number(settings.overtimeStartHours)) || Number(settings.overtimeStartHours) <= 0)) {
    return "Ange ett antal timmar större än 0 för när övertid börjar.";
  }
  if (settings.overtimeLimit && (!Number.isFinite(Number(settings.overtimeLimitHours)) || Number(settings.overtimeLimitHours) <= 0)) {
    return "Ange en övertidsgräns större än 0 timmar.";
  }
  if (settings.dailyRest && (!Number.isFinite(Number(settings.dailyRestHours)) || Number(settings.dailyRestHours) <= 0)) {
    return "Ange ett antal timmar större än 0 för dygnsvila.";
  }
  return "";
}

function shiftInterval(shift) {
  const date = shift.date?.toDate ? shift.date.toDate() : new Date(shift.date);
  const parseTime = (value) => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value || "");
    if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) return null;
    return [Number(match[1]), Number(match[2])];
  };
  const startTime = parseTime(shift.startTime);
  const endTime = parseTime(shift.endTime);
  if (!Number.isFinite(date.getTime()) || !startTime || !endTime) return null;
  const start = new Date(date);
  start.setHours(...startTime, 0, 0);
  const end = new Date(date);
  end.setHours(...endTime, 0, 0);
  if (end < start) end.setDate(end.getDate() + 1);
  return end > start ? { start, end } : null;
}

function dailyRestNotifications(employee, shifts, settings, currentDate) {
  const minimum = Number(settings.dailyRestHours);
  if (!settings.dailyRest || !Number.isFinite(minimum) || minimum <= 0) return [];
  const intervals = shifts.map(shiftInterval).filter(Boolean).sort((a, b) => a.start - b.start);
  const messages = [];
  const label = (date) => date.toLocaleString("sv-SE", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  let previous = null;
  for (const interval of intervals) {
    if (previous && interval.start <= previous.end) {
      if (interval.end > previous.end) previous = { ...previous, end: interval.end };
      continue;
    }
    if (previous) {
      const rest = (interval.start - previous.end) / 3600000;
      if (rest < minimum && interval.start.getFullYear() === currentDate.getFullYear() && interval.start.getMonth() === currentDate.getMonth()) {
        messages.push({
          id: `${employee.id}:rest:${previous.end.getTime()}:${interval.start.getTime()}:${minimum}`,
          name: employee.name || employee.userName || "Användare",
          title: "För kort dygnsvila mellan passen",
          text: `${hoursLabel(rest)} h ledigt mellan ${label(previous.end)} och ${label(interval.start)}. Inställd minsta vila: ${hoursLabel(minimum)} h.`,
        });
      }
    }
    previous = interval;
  }
  return messages;
}

export function buildScheduleNotifications({ settings, employees, shifts, workTimes, currentDate }) {
  if (!settings.enabled) return [];
  return employees.flatMap((employee) => {
    const workTime = employeeWorkTime(employee, workTimes);
    const employeeIds = identifiers(employee);
    const employeeShifts = shifts.filter((shift) => {
      if (shift.isObemannad || shift.approvedUnstaffedPlaceholder) return false;
      const shiftIds = [shift.employeeId, ...(shift.employeeIdentifiers || []), ...identifiers(shift.employeeData)]
        .filter(Boolean).map((value) => String(value).trim().toLowerCase());
      return employeeIds.some((id) => shiftIds.includes(id));
    });
    const messages = dailyRestNotifications(employee, employeeShifts, settings, currentDate);
    if (workTime?.percentage === 0 && workTime.monthlyHours === 0) return messages;
    const total = employeeShifts.reduce((sum, shift) => {
      const date = shift.date?.toDate ? shift.date.toDate() : new Date(shift.date);
      if (date.getFullYear() !== currentDate.getFullYear() || date.getMonth() !== currentDate.getMonth()) return sum;
      return sum + calculateShiftHours(shift, shift.employeeData) + calculateOvertimeForShift(shift, shift.employeeData).hours;
    }, 0);
    const target = monthlyWorkTimeHours(workTime);
    const name = employee.name || employee.userName || "Användare";
    const add = (type, threshold, title, text) => messages.push({
      id: `${employee.id}:${type}:${threshold}`, name, title, text,
    });
    if (settings.employment && workTime?.percentage < 100 && Number.isFinite(target) && target > 0 && total > target + 0.001) {
      add("employment", target, "Går nu över till mertid",
        `${hoursLabel(workTime.percentage)} % · ${workTimeHoursLabel(workTime)}. ${hoursLabel(total)} h schemalagt – ${hoursLabel(total - target)} h över arbetstidsmåttet för månaden (${hoursLabel(target)} h).`);
    }
    const start = Number(settings.overtimeStartHours);
    const limit = Number(settings.overtimeLimitHours);
    const overtime = total - start;
    if (Number.isFinite(start) && start > 0 && overtime > 0.001) {
      if (settings.overtime) add("overtime", start, "Går nu över till övertid",
        `${hoursLabel(total)} h schemalagt. Övertid börjar efter ${hoursLabel(start)} h: ${hoursLabel(overtime)} h över gränsen.`);
    }
    if (settings.overtimeLimit && Number.isFinite(limit) && limit > 0 && total > limit + 0.001) {
      add("limit", `total:${limit}`, "Övertidsmåttet har överskridits",
        `${hoursLabel(total)} h schemalagt – ${hoursLabel(total - limit)} h över din gräns på ${hoursLabel(limit)} h totalt för månaden.`);
    }
    return messages;
  });
}
